import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { api, LinkedInCluster, LinkedInClusterDetail } from '../../../services/api'
import { ClusterMap } from './ClusterMap'

/**
 * Topic clusters and their member posts.
 *
 * The clustering job and its endpoint both existed; nothing ever called them, so
 * the semantic layer was invisible.
 */

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 320px) 1fr;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ClusterRow = styled.button<{ $active: boolean }>`
  text-align: left;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 0.7rem 0.9rem;
  cursor: pointer;
  font: inherit;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const ClusterName = styled.div`
  font-weight: 600;
  margin-bottom: 0.2rem;
`

const Counts = styled.div`
  font-size: 0.78rem;
  opacity: 0.75;
  font-variant-numeric: tabular-nums;
`

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 1rem 1.2rem;
`

const PostRow = styled.div`
  padding: 0.7rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  &:last-child {
    border-bottom: none;
  }
`

const Meta = styled.div`
  font-size: 0.78rem;
  opacity: 0.75;
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
  font-variant-numeric: tabular-nums;
`

const Empty = styled.p`
  opacity: 0.75;
`

const Switch = styled.div`
  display: inline-flex;
  gap: 0.25rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const SwitchButton = styled.button<{ $active: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? 'white' : theme.colors.primary)};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.35rem 0.8rem;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
`

interface ClustersViewProps {
    /** Opens the shared detail modal for a post. */
    onOpenPost?: (postId: number) => void
}

export const ClustersView: React.FC<ClustersViewProps> = ({ onOpenPost }) => {
    const [clusters, setClusters] = useState<LinkedInCluster[]>([])
    const [selected, setSelected] = useState<LinkedInClusterDetail | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [view, setView] = useState<'list' | 'map'>('list')

    useEffect(() => {
        let cancelled = false
        api.linkedin
            .getClusters()
            .then((res) => {
                if (cancelled) return
                setClusters(res.data)
                if (res.data.length > 0) void open(res.data[0].id)
            })
            .catch(() => !cancelled && setError('No se pudieron cargar los clusteres.'))
            .finally(() => !cancelled && setLoading(false))
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const open = async (id: number) => {
        try {
            const res = await api.linkedin.getCluster(id)
            setSelected(res.data)
        } catch {
            setError('No se pudo cargar el detalle del cluster.')
        }
    }

    if (loading) return <Empty>Cargando clusteres…</Empty>
    if (error) return <Empty>{error}</Empty>
    if (clusters.length === 0) {
        return (
            <Empty>
                Todavía no hay clusteres. Se calculan de madrugada a partir de las publicaciones ya
                procesadas, y hacen falta varias que compartan tema.
            </Empty>
        )
    }

    return (
        <>
            <Switch role="tablist" aria-label="Vista de clusteres">
                <SwitchButton
                    type="button"
                    role="tab"
                    aria-selected={view === 'list'}
                    $active={view === 'list'}
                    onClick={() => setView('list')}
                >
                    Lista
                </SwitchButton>
                <SwitchButton
                    type="button"
                    role="tab"
                    aria-selected={view === 'map'}
                    $active={view === 'map'}
                    onClick={() => setView('map')}
                >
                    Mapa 2D
                </SwitchButton>
            </Switch>
            {view === 'map' && (
                <ClusterMap
                    onOpenPost={onOpenPost}
                    selectedClusterId={selected?.id ?? null}
                    onSelectCluster={(id) => void open(id)}
                />
            )}
            {view === 'list' && (
        <Layout>
            <List>
                {clusters.map((c) => (
                    <ClusterRow key={c.id} $active={selected?.id === c.id} onClick={() => open(c.id)}>
                        <ClusterName>{c.name}</ClusterName>
                        <Counts>
                            {c.post_count} publicaciones · {c.repo_count} repos
                        </Counts>
                    </ClusterRow>
                ))}
            </List>

            <Panel>
                {!selected ? (
                    <Empty>Selecciona un cluster.</Empty>
                ) : (
                    <>
                        <h3 style={{ marginTop: 0 }}>{selected.name}</h3>
                        {selected.keywords && selected.keywords.length > 0 && (
                            <Counts>{selected.keywords.join(' · ')}</Counts>
                        )}
                        {selected.posts.length === 0 ? (
                            <Empty>Este cluster no tiene publicaciones asignadas.</Empty>
                        ) : (
                            selected.posts.map((p) => (
                                <PostRow key={p.id}>
                                    <div
                                        style={{ cursor: onOpenPost ? 'pointer' : 'default', fontWeight: 600 }}
                                        onClick={() => onOpenPost?.(p.id)}
                                    >
                                        {p.author || 'Autor desconocido'}
                                    </div>
                                    <div style={{ fontSize: '0.9rem' }}>{p.summary || 'Sin resumen'}</div>
                                    <Meta>
                                        {typeof p.similarity === 'number' && (
                                            <span>afinidad {(p.similarity * 100).toFixed(0)}%</span>
                                        )}
                                        {typeof p.reactions === 'number' && <span>{p.reactions} reacciones</span>}
                                        {typeof p.relevance_score === 'number' && (
                                            <span>relevancia {p.relevance_score}</span>
                                        )}
                                    </Meta>
                                </PostRow>
                            ))
                        )}
                    </>
                )}
            </Panel>
        </Layout>
            )}
        </>
    )
}

export default ClustersView
